import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import {
  EmissionsCategoryEnum,
  PaginatedIngredientListList,
} from '@shared/api/types';
import { useGetStaticFilterOptions } from '@shared/components/data-table/hooks/use-get-static-filter-options';
import { parseAsInteger, useQueryState } from 'nuqs';
import { generatePath, useNavigate } from 'react-router-dom';
import { IngredientFilterColumn } from './filters';
import { useIngredientsTableColumns } from './ingredients-list-table-columns';

interface IngredientsTableProps
  extends DataTableProps<PaginatedIngredientListList> {}

export default function IngredientsTable({
  data,
  status,
  isLoading,
  isFetchedAfterMount,
  isPreviousData,
}: IngredientsTableProps) {
  const navigate = useNavigate();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { results: ingredients, count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { table, context } = useServerDataTable({
    data: ingredients || [],
    columns: useIngredientsTableColumns({
      filters: {
        [IngredientFilterColumn.EmissionsCategory]: useGetStaticFilterOptions(
          EmissionsCategoryEnum
        ),
      },
    }),
    getRowId: (row) => row.uuid,
    pageCount,
    initialState: {
      columnVisibility: {
        baseFoodUuid: false,
        [IngredientFilterColumn.EmissionsCategory]: false,
      },
    },
  });

  return (
    <DataTable
      table={table}
      context={context}
      isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      tableLoading={!data && status === 'loading'}
      className="bg-gray-00"
      rowClassName={(row) => 'hover:bg-gray-50 cursor-pointer'}
      rowClickColumnDisabledIds={(row) => [
        ...(row.original.purchasedGoodUuid ? ['matchedFood'] : []),
      ]}
      onRowClick={(row) => {
        navigate(
          generatePath(pcfRoutes.PRODUCT_COMPONENTS_INGREDIENTS_DETAIL, {
            ingredient_uuid: row?.original.uuid,
          })
        );
      }}
    >
      <DataTableToolbar table={table} />
    </DataTable>
  );
}
